import React, { useEffect, useMemo, useRef, useState } from "react";
import { useCookies } from 'react-cookie';
import AdminSharedPresetPanel from "./AdminSharedPresetPanel";
import OpenPositionsSnapshotSection from "./OpenPositionsSnapshotSection";
import SymbolSearchPopup from "./SymbolSearchPopup";


export default function SetPage(){

    const [cookies] = useCookies(['uuid']);

    // 쿠키에서 UUID를 이용하여 사용자 정보 가져오기
    const[uid,setUID] = useState(0);
    const[nickname,setNickname] = useState('');
    const [isAdminAccess, setIsAdminAccess] = useState(false);

    const [isChecked, setIsChecked] = useState(false);

    const [symbolList, setSymbolList] = useState([]);

    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [popupAnchor, setPopupAnchor] = useState(null);
    const [isSymbolMenuOpen, setIsSymbolMenuOpen] = useState(false);
    const [isModeMenuOpen, setIsModeMenuOpen] = useState(false);
    const symbolDropdownRef = useRef(null);
    const modeDropdownRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (symbolDropdownRef.current && !symbolDropdownRef.current.contains(event.target)) {
          setIsSymbolMenuOpen(false);
        }
        if (modeDropdownRef.current && !modeDropdownRef.current.contains(event.target)) {
          setIsModeMenuOpen(false);
        }
      };

      const handleEscapeKey = (event) => {
        if (event.key === 'Escape') {
          setIsSymbolMenuOpen(false);
          setIsModeMenuOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }, []);
  
    useEffect(() => {
      if (!cookies.uuid) {
        redirectToLogin();
        return;
      }

      // uuid가 있을 때 서버로 요청 (authmiddleware가 처리)
      fetchUserInfo();
    }, [cookies.uuid]);

    const redirectToLogin = () => {
      alert('로그인 정보가 존재하지 않습니다. 로그인 페이지로 이동합니다.');
      window.location.href = '/LoginAts';
    };
  
    const fetchUserInfo = () => {
      fetch(`/circleproversion_selected`, { credentials: 'include' })
        .then(async (res) => {
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error(data.message || '페이지 접근 권한 확인에 실패했습니다.');
          }
          return data;
        })
        .then((data) => {
          const adminAccess = Boolean(data?.is_admin_access);
          setIsAdminAccess(adminAccess);
          if (data.message !== 'ok') {
            alert(data.message);
            window.location.href = adminAccess ? '/Adminpage' : '/SelectPage';
            return null;
          }
          return fetch('/GetUserInfo', { credentials: 'include' });
        })
        .then((res) => {
          if (!res) {
            return null;
          }
          return res.json();
        })
        .then((user) => {
          if (!user) {
            return;
          }
          setIsAdminAccess(Boolean(user.is_admin_access));
          setUID(user.uid);
          setNickname(user.nickname);
          fetchLists();
        })
        .catch(() => {
          redirectToLogin();
        });
    };

    // selectedlevel, entry, waiting list 받아오기
    const fetchLists = () => {
      fetch(`/startcheck/searchlevel`)
        .then(response => {
          if (!response.ok) {
            throw new Error('서버 응답 실패');
          }
          return response.json();
        })
        .then(data => {
          if(data[0].super_user === "8"){
            setLevelCheck(data[0].super_user);  
          }else{
            setLevelCheck(data[0].selectedLevel);
          }
        })
        .catch(error => {
          console.error('Entry List 가져오기 오류:', error);
          alert(error);
        });

      fetch(`/startcheck_circlepro/list_entry`)
        .then(response => {
          if (!response.ok) {
            throw new Error('서버 응답 실패');
          }
          return response.json();
        })
        .then(data => {
          updateList(data, e_items, setE_Items);
        })
        .catch(error => {
          console.error('Entry List 가져오기 오류:', error);
          alert(error);
        });
  
      fetch(`/startcheck_circlepro/list_wait`)
        .then(response => {
          if (!response.ok) {
            throw new Error('서버 응답 실패');
          }
          return response.json();
        })
        .then(data => {
          updateList(data, w_items, setW_Items);
        })
        .catch(error => {
          console.error('Waiting List 가져오기 오류:', error);
          alert(error);
        });
    };
  
    const updateList = (data, list, setList) => {
      for (let i = 0; i < data.length; i++) {
        if (!isItemInList(data[i].symbol, list)) {
          setList(prevList => [...prevList, data[i].symbol]);
        }
      }
    };

    const isItemInList = (item, list) => {
      return list.includes(item);
    };

    const[seed,setSeed] = useState('basic');

    const[mode,setMode] = useState('normal');

    const[auto_count,setSymbolCount] = useState(3);
    const[top_bottom,setTopBottom] = useState('top');
    const[fluctuation,setFluctuation] = useState(0);

    const[funding_rate,setFundingRate] = useState('disable');
    const[f_percent_low,setFpercentLow] = useState(-100);
    const[f_percent_high,setFpercentHigh] = useState(0);

    const[symbol,setSymbol] = useState('default');
    const selectableSymbols = useMemo(
      () => Array.from(new Set(symbolList.filter((sym) => sym && sym !== 'default'))),
      [symbolList]
    );
    const currentSymbolLabel = symbol && symbol !== 'default' ? symbol : 'symbol select';
    const MODE_OPTIONS = [
      { value: 'normal', label: '일반모드 셋팅' },
      { value: 'auto', label: '자동모드 셋팅' },
    ];
    const currentModeLabel = MODE_OPTIONS.find((option) => option.value === mode)?.label || '일반모드 셋팅';

    useEffect(() => {
      if (mode === 'auto') {
        setIsSymbolMenuOpen(false);
      }
    }, [mode]);

    const toggleModeMenu = () => {
      setIsModeMenuOpen((prev) => !prev);
    };

    const toggleSymbolMenu = () => {
      if (mode === 'auto') {
        return;
      }
      setIsSymbolMenuOpen((prev) => !prev);
    };

    const handleModeSelect = (value) => {
      setIsModeMenuOpen(false);
      if (value === mode) {
        return;
      }
      setMode(value);
      if (value === 'auto') {
        onClickSearch_auto();
      }
    };

    const handleSymbolSelect = (value) => {
      if (mode === 'auto') {
        return;
      }
      setSymbol(value);
      if (value && value !== 'default') {
        onClickSearch(value);
      }
      setIsSymbolMenuOpen(false);
    };

    const handleOpenPopup = (event) => {
      if (mode === 'auto') {
        return;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const popupWidth = 300;
      const popupHeight = 320;

      const desiredTop = rect.top + scrollTop - popupHeight - 16;
      const top = Math.max(scrollTop + 20, desiredTop);
      const minLeft = scrollLeft + 16;
      const maxLeft = scrollLeft + viewportWidth - popupWidth - 16;
      const candidateLeft = rect.left + scrollLeft;
      const left = Math.min(Math.max(candidateLeft, minLeft), maxLeft);

      setPopupAnchor({ top, left });
      setIsPopupOpen(true);
    };

    const[leverage_info, setLeverage] = useState(20);

    const[sensitivity,setSensitivity] = useState(2);
    const[atr_period,setAtrPeriod] = useState(2);

    const[basic_mode,setBasicMode] = useState('normal');
    const[entry_continue,setEntryContinue] = useState('disable');

    const[entry_standard,setEntryStandard] = useState('disable');
    const[entry_signal,setEntrySignal] = useState('ut');

    const[candle_standard,setCandleStandard] = useState('4h');
    const[candle_type_standard, setCandleTypeStandard] = useState('haikin');
    const[sensitivity_standard,setSensitivityStandard] = useState(2);
    const[atr_period_standard,setAtrPeriodStandard] = useState(2);

    const[ema_standard,setEmaStandard] = useState(200);
    const[ema_losscut,setEmaLosscut] = useState('no');    

    const[sensitivity_close,setSensitivityClose] = useState(1);
    const[atr_period_close,setAtrPeriodClose] = useState(1);

    const[entry_mode,setEntryMode] = useState('ut');
    const[hedge_mode,setHedgeMode] = useState('ut');
    
    const[entry_candle,setEntryCandle] = useState('5m');

    const[entry_checking,setEntryChecking] = useState(-1);
    const[standard_checking,setStandardChecking] = useState(-1);
    const[close_checking,setCloseChecking] = useState(-1);

    const[close_mode,setCloseMode] = useState('ut');

    const[sensitivity_hedge,setSensitivityHedge] = useState(2);
    const[atr_period_hedge,setAtrPeriodHedge] = useState(1);

    const[hedge_candle,setHedgeCandle] = useState('5m');
    const[candle_type_hedge, setCandleTypeHedge] = useState('haikin');
    const[hedge_checking,setHedgeChecking] = useState(-1);

    //////////////////////////////////////////////////////////

    const[close_candle,setCloseCandle] = useState('15m');

    const[direction,setDirection] = useState('long');

    const[circle_benefit_mode,setCircleBenefitMode] = useState('normal');

    //////////////////////////////////////////////////////////

    const[candle_type_entry, setCandleTypeEntry] = useState('haikin');
    const[candle_type_close, setCandleTypeClose] = useState('haikin');

    const[benefit_ratio,setBenefitRatio] = useState(0.3);

    const[trigger_ratio,setTriggerRatio] = useState(1);

    const[long_count,setLongCount] = useState(0)
    const[short_count,setShortCount] = useState(0)

    const[entry_usdt_1, setEntryUsdt_1] = useState(10);
    const[entry_usdt_2, setEntryUsdt_2] = useState(10);
    const[entry_usdt_3, setEntryUsdt_3] = useState(10);
    const[entry_usdt_4, setEntryUsdt_4] = useState(0);
    const[entry_usdt_5, setEntryUsdt_5] = useState(0);
    const[entry_usdt_6, setEntryUsdt_6] = useState(0);
    const[entry_usdt_7, setEntryUsdt_7] = useState(0);
    const[entry_usdt_8, setEntryUsdt_8] = useState(0);
    const[entry_usdt_9, setEntryUsdt_9] = useState(0);
    const[entry_usdt_10, setEntryUsdt_10] = useState(0);
    const[entry_usdt_11, setEntryUsdt_11] = useState(0);
    const[entry_usdt_12, setEntryUsdt_12] = useState(0);
    const[entry_usdt_13, setEntryUsdt_13] = useState(0);
    const[entry_usdt_14, setEntryUsdt_14] = useState(0);
    const[entry_usdt_15, setEntryUsdt_15] = useState(0);
    const[entry_usdt_16, setEntryUsdt_16] = useState(0);
    const[entry_usdt_17, setEntryUsdt_17] = useState(0);
    const[entry_usdt_18, setEntryUsdt_18] = useState(0);
    const[entry_usdt_19, setEntryUsdt_19] = useState(0);
    const[entry_usdt_20, setEntryUsdt_20] = useState(0);
    const[entry_usdt_21, setEntryUsdt_21] = useState(0);
    const[entry_usdt_22, setEntryUsdt_22] = useState(0);
    const[entry_usdt_23, setEntryUsdt_23] = useState(0);
    const[entry_usdt_24, setEntryUsdt_24] = useState(0);


    const[entry_ratio_2, setEntryRatio_2] = useState(0.5);
    const[entry_ratio_3, setEntryRatio_3] = useState(0.5);
    const[entry_ratio_4, setEntryRatio_4] = useState(0);
    const[entry_ratio_5, setEntryRatio_5] = useState(0);
    const[entry_ratio_6, setEntryRatio_6] = useState(0);
    const[entry_ratio_7, setEntryRatio_7] = useState(0);
    const[entry_ratio_8, setEntryRatio_8] = useState(0);
    const[entry_ratio_9, setEntryRatio_9] = useState(0);
    const[entry_ratio_10, setEntryRatio_10] = useState(0);
    const[entry_ratio_11, setEntryRatio_11] = useState(0);
    const[entry_ratio_12, setEntryRatio_12] = useState(0);
    const[entry_ratio_13, setEntryRatio_13] = useState(0);
    const[entry_ratio_14, setEntryRatio_14] = useState(0);
    const[entry_ratio_15, setEntryRatio_15] = useState(0);
    const[entry_ratio_16, setEntryRatio_16] = useState(0);
    const[entry_ratio_17, setEntryRatio_17] = useState(0);
    const[entry_ratio_18, setEntryRatio_18] = useState(0);
    const[entry_ratio_19, setEntryRatio_19] = useState(0);
    const[entry_ratio_20, setEntryRatio_20] = useState(0);
    const[entry_ratio_21, setEntryRatio_21] = useState(0);
    const[entry_ratio_22, setEntryRatio_22] = useState(0);
    const[entry_ratio_23, setEntryRatio_23] = useState(0);
    const[entry_ratio_24, setEntryRatio_24] = useState(0);

    //////////////////////////////////////////////////////////

    const[symbolCountValid, setSymbolCountValid] = useState(true);
    const[fPercentLowValid, setFpercentLowValid] = useState(true);
    const[fPercentHighValid, setFpercentHighValid] = useState(true);
    const[fluctuationValid, setFluctuationValid] = useState(true);
    
    const[leverageValid, setLeverageValid] = useState(true);
  
    const[emaStandardValid, setEmaStandardValid] = useState(true);

    const[sensitivityStandardValid, setSensitivityStandardValid] = useState(true);
    const[atrperiodStandardValid, setAtrperiodStandardValid] = useState(true);

    const[sensitivityValid, setSensitivityValid] = useState(true);
    const[atrperiodValid, setAtrperiodValid] = useState(true);
  
    const[sensitivityHedgeValid, setSensitivityHedgeValid] = useState(true);
    const[atrperiodHedgeValid, setAtrperiodHedgeValid] = useState(true);

    const[sensitivityCloseValid, setSensitivityCloseValid] = useState(true);
    const[atrperiodCloseValid, setAtrperiodCloseValid] = useState(true);

    const[benefit_ratioratioValid, setBenefitRatioratioValid] = useState(true);

    const[trigger_ratioValid, setTriggerRatioValid] = useState(true);

    const[entryusdtValid_1,setEntryUsdtValid_1] = useState(true);
    const[entryusdtValid_2,setEntryUsdtValid_2] = useState(true);
    const[entryusdtValid_3,setEntryUsdtValid_3] = useState(true);
    const[entryusdtValid_4,setEntryUsdtValid_4] = useState(true);
    const[entryusdtValid_5,setEntryUsdtValid_5] = useState(true);
    const[entryusdtValid_6,setEntryUsdtValid_6] = useState(true);
    const[entryusdtValid_7,setEntryUsdtValid_7] = useState(true);
    const[entryusdtValid_8,setEntryUsdtValid_8] = useState(true);
    const[entryusdtValid_9,setEntryUsdtValid_9] = useState(true);
    const[entryusdtValid_10,setEntryUsdtValid_10] = useState(true);
    const[entryusdtValid_11,setEntryUsdtValid_11] = useState(true);
    const[entryusdtValid_12,setEntryUsdtValid_12] = useState(true);
    const[entryusdtValid_13,setEntryUsdtValid_13] = useState(true);
    const[entryusdtValid_14,setEntryUsdtValid_14] = useState(true);
    const[entryusdtValid_15,setEntryUsdtValid_15] = useState(true);
    const[entryusdtValid_16,setEntryUsdtValid_16] = useState(true);
    const[entryusdtValid_17,setEntryUsdtValid_17] = useState(true);
    const[entryusdtValid_18,setEntryUsdtValid_18] = useState(true);
    const[entryusdtValid_19,setEntryUsdtValid_19] = useState(true);
    const[entryusdtValid_20,setEntryUsdtValid_20] = useState(true);
    const[entryusdtValid_21,setEntryUsdtValid_21] = useState(true);
    const[entryusdtValid_22,setEntryUsdtValid_22] = useState(true);
    const[entryusdtValid_23,setEntryUsdtValid_23] = useState(true);
    const[entryusdtValid_24,setEntryUsdtValid_24] = useState(true);

    const[entryratioValid_2,setEntryRatioValid_2] = useState(true);
    const[entryratioValid_3,setEntryRatioValid_3] = useState(true);
    const[entryratioValid_4,setEntryRatioValid_4] = useState(true);
    const[entryratioValid_5,setEntryRatioValid_5] = useState(true);
    const[entryratioValid_6,setEntryRatioValid_6] = useState(true);
    const[entryratioValid_7,setEntryRatioValid_7] = useState(true);
    const[entryratioValid_8,setEntryRatioValid_8] = useState(true);
    const[entryratioValid_9,setEntryRatioValid_9] = useState(true);
    const[entryratioValid_10,setEntryRatioValid_10] = useState(true);
    const[entryratioValid_11,setEntryRatioValid_11] = useState(true);
    const[entryratioValid_12,setEntryRatioValid_12] = useState(true);
    const[entryratioValid_13,setEntryRatioValid_13] = useState(true);
    const[entryratioValid_14,setEntryRatioValid_14] = useState(true);
    const[entryratioValid_15,setEntryRatioValid_15] = useState(true);
    const[entryratioValid_16,setEntryRatioValid_16] = useState(true);
    const[entryratioValid_17,setEntryRatioValid_17] = useState(true);
    const[entryratioValid_18,setEntryRatioValid_18] = useState(true);
    const[entryratioValid_19,setEntryRatioValid_19] = useState(true);
    const[entryratioValid_20,setEntryRatioValid_20] = useState(true);
    const[entryratioValid_21,setEntryRatioValid_21] = useState(true);
    const[entryratioValid_22,setEntryRatioValid_22] = useState(true);
    const[entryratioValid_23,setEntryRatioValid_23] = useState(true);
    const[entryratioValid_24,setEntryRatioValid_24] = useState(true);

    const[setlevel, setLevelCheck] = useState(0);

    const[notAllow, setNotAllow] = useState(true);

    const[e_items, setE_Items] = useState([]);
    const[w_items, setW_Items] = useState([]);

    const handleCheckboxChange = (event) => {
      setIsChecked(event.target.checked);
    };

    const[trade_start,setTradeStart] = useState(false);
    const[benefit_stop,setBenefit_stop] = useState(false);
    const[benefit_cancel,setBenefit_cancel] = useState(false);

    const[currentCoin_stop,setCurrentCoin_stop] = useState(false);
    const[allCoin_stop,setAllCoin_stop] = useState(false);

    const[end,setEnd] = useState(false);
    
    // 유효성 검사 //// 유효성 검사 //// 유효성 검사 //// 유효성 검사 //// 유효성 검사 //// 유효성 검사 //

    const handleSymbolCount = (e) => {
      const newcount = e.target.value.trim();
      setSymbolCount(newcount);
        const regex = 
        /^\d*$/;
        if (regex.test(newcount)) {
          setSymbolCountValid(true);
          } 
        else {
          setSymbolCountValid(false);
          }
        };

    const handleTopBotton = (e) => {
      setTopBottom(e.target.value);
        };

    const handleFundingRate = (e) => {
      setFundingRate(e.target.value);
        };

    const handleFpercentLow = (e) => {
      const newPercent = e.target.value.trim();
      setFpercentLow(newPercent);

      const regex = /^-?\d*(\.\d+)?$/; // ✅ 정수와 실수(음수 포함) 허용
      if (regex.test(newPercent)) {
        setFpercentLowValid(true);
      } else {
        setFpercentLowValid(false);
      }
    };

    const handleFpercentHigh = (e) => {
      const newPercent = e.target.value.trim();
      setFpercentHigh(newPercent);

      const regex = /^-?\d*(\.\d+)?$/; // ✅ 정수와 실수(음수 포함) 허용
      if (regex.test(newPercent)) {
        setFpercentHighValid(true);
      } else {
        setFpercentHighValid(false);
      }
    };

    const handleFluctuation = (e) => {
      const newPercent = e.target.value.trim();
      setFluctuation(newPercent);

      const regex = /^-?\d*(\.\d+)?$/; // ✅ 정수와 실수(음수 포함) 허용
      if (regex.test(newPercent)) {
        setFluctuationValid(true);
      } else {
        setFluctuationValid(false);
      }
    };

    const handleEntryCandle = (e) => {
      setEntryCandle(e.target.value);
        };
    
    const handleEntryMode = (e) => {
      setEntryMode(e.target.value);
        };
    
    const handleHedgeMode = (e) => {
      setHedgeMode(e.target.value);
        };
      
    const handleBasicMode = (e) => {
      setBasicMode(e.target.value);
        };

    const handleEntryContinue = (e) => {
      setEntryContinue(e.target.value);
        };
    
    const handleEntryStandard = (e) => {
      setEntryStandard(e.target.value);
    };

    const handleEntrySignal = (e) => {
      setEntrySignal(e.target.value);
        };
    
    const handleEmaLosscut = (e) => {
      setEmaLosscut(e.target.value);
        };

    const handleCandleStandard = (e) => {
      setCandleStandard(e.target.value);
    };

    const handleCandleTypeStandard = (e) => {
      setCandleTypeStandard(e.target.value);
    };
      
    const handleEntryChecking = (e) => {
      setEntryChecking(e.target.value);
        };

    const handleStandardChecking = (e) => {
      setStandardChecking(e.target.value);
        };
  
    const handleCloseChecking = (e) => {
      setCloseChecking(e.target.value);
        };
            
    const handleCloseMode = (e) => {
      setCloseMode(e.target.value);
        };

    const handleCloseCandle = (e) => {
      setCloseCandle(e.target.value);
        };

    const handleHedgeChecking = (e) => {
      setHedgeChecking(e.target.value);
        };
            
    const handleHedgeCandle = (e) => {
      setHedgeCandle(e.target.value);
        };
    
    const handleDirection = (e) => {
      setDirection(e.target.value);
        };

    const handleCircleBenefitMode = (e) => {
      setCircleBenefitMode(e.target.value);
        };
    
    const handleCandleTypeEntry = (e) => {
      setCandleTypeEntry(e.target.value);
        };

    const handleCandleTypeHedge = (e) => {
      setCandleTypeHedge(e.target.value);
        };
    
    const handleCandleTypeClose = (e) => {
      setCandleTypeClose(e.target.value);
        };

    const handleLeverage = (e) => {
      const newLeverage = e.target.value.trim();
      setLeverage(newLeverage);
        const regex = 
        /^\d*$/;
        if (regex.test(newLeverage)) {
          setLeverageValid(true);
          } 
        else {
          setLeverageValid(false);
          }
        };

    const handleEmaStandard = (e) => {
      const newemastandard = e.target.value.trim();
      setEmaStandard(newemastandard);
        const regex = 
        /^\d*$/;
        if (regex.test(newemastandard)) {
          setEmaStandardValid(true);
          } 
        else {
          setEmaStandardValid(false);
          }
        };

    const handleSensitivity = (e) => {
      const newSensitivity = e.target.value.trim();
      setSensitivity(newSensitivity);
        const regex = 
        /^\d+$/;
        if (regex.test(newSensitivity)) {
          setSensitivityValid(true);
          } 
        else {
          setSensitivityValid(false);
          }
        };
    
    const handleAtrperiod = (e) => {
      const newdata = e.target.value.trim();
      setAtrPeriod(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setAtrperiodValid(true);
          } 
        else {
          setAtrperiodValid(false);
          }
        };
  
    const handleSensitivityStandard = (e) => {
      const newSensitivity = e.target.value.trim();
      setSensitivityStandard(newSensitivity);
        const regex = 
        /^\d+$/;
        if (regex.test(newSensitivity)) {
          setSensitivityStandardValid(true);
          } 
        else {
          setSensitivityStandardValid(false);
          }
        };
    
    const handleAtrperiodStandard = (e) => {
      const newdata = e.target.value.trim();
      setAtrPeriodStandard(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setAtrperiodStandardValid(true);
          } 
        else {
          setAtrperiodStandardValid(false);
          }
        };

    const handleSensitivityHedge = (e) => {
      const newSensitivity = e.target.value.trim();
      setSensitivityHedge(newSensitivity);
        const regex = 
        /^\d+$/;
        if (regex.test(newSensitivity)) {
          setSensitivityHedgeValid(true);
          } 
        else {
          setSensitivityHedgeValid(false);
          }
        };
    
    const handleAtrperiodHedge = (e) => {
      const newdata = e.target.value.trim();
      setAtrPeriodHedge(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setAtrperiodHedgeValid(true);
          } 
        else {
          setAtrperiodHedgeValid(false);
          }
        };

    const handleSensitivityClose = (e) => {
      const newSensitivity = e.target.value.trim();
      setSensitivityClose(newSensitivity);
        const regex = 
        /^\d+$/;
        if (regex.test(newSensitivity)) {
          setSensitivityCloseValid(true);
          } 
        else {
          setSensitivityCloseValid(false);
          }
        };
    
    const handleAtrperiodClose = (e) => {
      const newdata = e.target.value.trim();
      setAtrPeriodClose(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setAtrperiodCloseValid(true);
          } 
        else {
          setAtrperiodCloseValid(false);
          }
        };

    const handleBenefitratio = (e) => {
      const newBenefitratio = e.target.value.trim();
      setBenefitRatio(newBenefitratio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newBenefitratio)) {
          setBenefitRatioratioValid(true);
          } 
        else {
          setBenefitRatioratioValid(false);
          }
        };

    const handleTriggerratio = (e) => {
      const newTriggerratio = e.target.value.trim();
      setTriggerRatio(newTriggerratio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newTriggerratio)) {
          setTriggerRatioValid(true);
          } 
        else {
          setTriggerRatioValid(false);
          }
        };
    
    
    /////////////////////////////////////////////////////////////////
    /////////////////////////////////////////////////////////////////

    // 진입 USDT 유효성 검사 //// 진입 USDT 유효성 검사 //// 진입 USDT 유효성 검사 //
    const handleEntryUsdt_1 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_1(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_1(true);
          } 
        else {
          setEntryUsdtValid_1(false);
          }
        };
    const handleEntryUsdt_2 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_2(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_2(true);
          } 
        else {
          setEntryUsdtValid_2(false);
          }
        };
    const handleEntryUsdt_3 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_3(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_3(true);
          } 
        else {
          setEntryUsdtValid_3(false);
          }
        };
    const handleEntryUsdt_4 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_4(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_4(true);
          } 
        else {
          setEntryUsdtValid_4(false);
          }
        };
    const handleEntryUsdt_5 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_5(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_5(true);
          } 
        else {
          setEntryUsdtValid_5(false);
          }
        };
    const handleEntryUsdt_6 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_6(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_6(true);
          } 
        else {
          setEntryUsdtValid_6(false);           
          }
        };
    const handleEntryUsdt_7 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_7(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_7(true);
          } 
        else {
          setEntryUsdtValid_7(false);
          }
        };
    const handleEntryUsdt_8 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_8(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_8(true);
          } 
        else {
          setEntryUsdtValid_8(false);
          }
        };
    const handleEntryUsdt_9 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_9(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_9(true);
          } 
        else {
          setEntryUsdtValid_9(false);
          }
        };
    const handleEntryUsdt_10 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_10(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_10(true);
          } 
        else {
          setEntryUsdtValid_10(false);
          }
        };
    const handleEntryUsdt_11 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_11(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_11(true);
          } 
        else {
          setEntryUsdtValid_11(false);
          }
        };
    const handleEntryUsdt_12 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_12(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_12(true);
          } 
        else {
          setEntryUsdtValid_12(false);
          }
        };
    const handleEntryUsdt_13 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_13(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_13(true);
          } 
        else {
          setEntryUsdtValid_13(false);
          }
        };
    const handleEntryUsdt_14 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_14(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_14(true);
          } 
        else {
          setEntryUsdtValid_14(false);
          }
        };
    const handleEntryUsdt_15 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_15(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_15(true);
          } 
        else {
          setEntryUsdtValid_15(false);
          }
        };
    const handleEntryUsdt_16 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_16(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_16(true);
          } 
        else {
          setEntryUsdtValid_16(false);
          }
        };
    const handleEntryUsdt_17 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_17(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_17(true);
          } 
        else {
          setEntryUsdtValid_17(false);
          }
        };
    const handleEntryUsdt_18 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_18(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_18(true);
          } 
        else {
          setEntryUsdtValid_18(false);
          }
        };
    const handleEntryUsdt_19 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_19(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_19(true);
          } 
        else {
          setEntryUsdtValid_19(false);
          }
        };
    const handleEntryUsdt_20 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_20(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_20(true);
          } 
        else {
          setEntryUsdtValid_20(false);
          }
        };
    const handleEntryUsdt_21 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_21(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_21(true);
          } 
        else {
          setEntryUsdtValid_21(false);
          }
        };
    const handleEntryUsdt_22 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_22(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_22(true);
          } 
        else {
          setEntryUsdtValid_22(false);
          }
        };
    const handleEntryUsdt_23 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_23(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_23(true);
          } 
        else {
          setEntryUsdtValid_23(false);
          }
        };
    const handleEntryUsdt_24 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_24(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_24(true);
          } 
        else {
          setEntryUsdtValid_24(false);
          }
        };
      
    

    // 진입 RATIO 유효성 검사 //// 진입 RATIO 유효성 검사 //// 진입 RATIO 유효성 검사 //
    const handleEntryRatio_2 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_2(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_2(true);
          } 
        else {
          setEntryRatioValid_2(false);
          }
        };
    const handleEntryRatio_3 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_3(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_3(true);
          } 
        else {
          setEntryRatioValid_3(false);
          }
        };
    const handleEntryRatio_4 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_4(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_4(true);
          } 
        else {
          setEntryRatioValid_4(false);
          }
        };
    const handleEntryRatio_5 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_5(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_5(true);
          } 
        else {
          setEntryRatioValid_5(false);
          }
        };
    const handleEntryRatio_6 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_6(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_6(true);
          } 
        else {
          setEntryRatioValid_6(false);           
          }
        };
    const handleEntryRatio_7 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_7(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_7(true);
          } 
        else {
          setEntryRatioValid_7(false);
          }
        };
    const handleEntryRatio_8 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_8(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_8(true);
          } 
        else {
          setEntryRatioValid_8(false);
          }
        };
    const handleEntryRatio_9 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_9(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_9(true);
          } 
        else {
          setEntryRatioValid_9(false);
          }
        };
    const handleEntryRatio_10 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_10(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_10(true);
          } 
        else {
          setEntryRatioValid_10(false);
          }
        };
    const handleEntryRatio_11 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_11(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_11(true);
          } 
        else {
          setEntryRatioValid_11(false);
          }
        };
    const handleEntryRatio_12 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_12(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_12(true);
          } 
        else {
          setEntryRatioValid_12(false);
          }
        };
    const handleEntryRatio_13 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_13(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_13(true);
          } 
        else {
          setEntryRatioValid_13(false);
          }
        };
    const handleEntryRatio_14 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_14(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_14(true);
          } 
        else {
          setEntryRatioValid_14(false);
          }
        };
    const handleEntryRatio_15 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_15(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_15(true);
          } 
        else {
          setEntryRatioValid_15(false);
          }
        };
    const handleEntryRatio_16 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_16(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_16(true);
          } 
        else {
          setEntryRatioValid_16(false);
          }
        };
    const handleEntryRatio_17 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_17(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_17(true);
          } 
        else {
          setEntryRatioValid_17(false);
          }
        };
    const handleEntryRatio_18 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_18(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_18(true);
          } 
        else {
          setEntryRatioValid_18(false);
          }
        };
    const handleEntryRatio_19 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_19(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_19(true);
          } 
        else {
          setEntryRatioValid_19(false);
          }
        };
    const handleEntryRatio_20 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_20(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_20(true);
          } 
        else {
          setEntryRatioValid_20(false);
          }
        };
    const handleEntryRatio_21 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_21(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_21(true);
          } 
        else {
          setEntryRatioValid_21(false);
          }
        };
    const handleEntryRatio_22 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_22(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_22(true);
          } 
        else {
          setEntryRatioValid_22(false);
          }
        };
    const handleEntryRatio_23 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_23(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_23(true);
          } 
        else {
          setEntryRatioValid_23(false);
          }
        };
    const handleEntryRatio_24 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_24(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_24(true);
          } 
        else {
          setEntryRatioValid_24(false);
          }
        };  

    const circleEntryRows = [
      { label: "1st set", usdtValue: entry_usdt_1, onChangeUsdt: handleEntryUsdt_1, hideRatio: true },
      { label: "2nd set", usdtValue: entry_usdt_2, onChangeUsdt: handleEntryUsdt_2, ratioValue: entry_ratio_2, onChangeRatio: handleEntryRatio_2 },
      { label: "3rd set", usdtValue: entry_usdt_3, onChangeUsdt: handleEntryUsdt_3, ratioValue: entry_ratio_3, onChangeRatio: handleEntryRatio_3 },
      { label: "4th set", usdtValue: entry_usdt_4, onChangeUsdt: handleEntryUsdt_4, ratioValue: entry_ratio_4, onChangeRatio: handleEntryRatio_4 },
      { label: "5th set", usdtValue: entry_usdt_5, onChangeUsdt: handleEntryUsdt_5, ratioValue: entry_ratio_5, onChangeRatio: handleEntryRatio_5 },
      { label: "6th set", usdtValue: entry_usdt_6, onChangeUsdt: handleEntryUsdt_6, ratioValue: entry_ratio_6, onChangeRatio: handleEntryRatio_6 },
      { label: "7th set", usdtValue: entry_usdt_7, onChangeUsdt: handleEntryUsdt_7, ratioValue: entry_ratio_7, onChangeRatio: handleEntryRatio_7 },
      { label: "8th set", usdtValue: entry_usdt_8, onChangeUsdt: handleEntryUsdt_8, ratioValue: entry_ratio_8, onChangeRatio: handleEntryRatio_8 },
      { label: "9th set", usdtValue: entry_usdt_9, onChangeUsdt: handleEntryUsdt_9, ratioValue: entry_ratio_9, onChangeRatio: handleEntryRatio_9 },
      { label: "10th set", usdtValue: entry_usdt_10, onChangeUsdt: handleEntryUsdt_10, ratioValue: entry_ratio_10, onChangeRatio: handleEntryRatio_10 },
      { label: "11th set", usdtValue: entry_usdt_11, onChangeUsdt: handleEntryUsdt_11, ratioValue: entry_ratio_11, onChangeRatio: handleEntryRatio_11 },
      { label: "12th set", usdtValue: entry_usdt_12, onChangeUsdt: handleEntryUsdt_12, ratioValue: entry_ratio_12, onChangeRatio: handleEntryRatio_12 },
      { label: "13th set", usdtValue: entry_usdt_13, onChangeUsdt: handleEntryUsdt_13, ratioValue: entry_ratio_13, onChangeRatio: handleEntryRatio_13 },
      { label: "14th set", usdtValue: entry_usdt_14, onChangeUsdt: handleEntryUsdt_14, ratioValue: entry_ratio_14, onChangeRatio: handleEntryRatio_14 },
      { label: "15th set", usdtValue: entry_usdt_15, onChangeUsdt: handleEntryUsdt_15, ratioValue: entry_ratio_15, onChangeRatio: handleEntryRatio_15 },
      { label: "16th set", usdtValue: entry_usdt_16, onChangeUsdt: handleEntryUsdt_16, ratioValue: entry_ratio_16, onChangeRatio: handleEntryRatio_16 },
      { label: "17th set", usdtValue: entry_usdt_17, onChangeUsdt: handleEntryUsdt_17, ratioValue: entry_ratio_17, onChangeRatio: handleEntryRatio_17 },
      { label: "18th set", usdtValue: entry_usdt_18, onChangeUsdt: handleEntryUsdt_18, ratioValue: entry_ratio_18, onChangeRatio: handleEntryRatio_18 },
      { label: "19th set", usdtValue: entry_usdt_19, onChangeUsdt: handleEntryUsdt_19, ratioValue: entry_ratio_19, onChangeRatio: handleEntryRatio_19 },
      { label: "20th set", usdtValue: entry_usdt_20, onChangeUsdt: handleEntryUsdt_20, ratioValue: entry_ratio_20, onChangeRatio: handleEntryRatio_20 },
      { label: "21th set", usdtValue: entry_usdt_21, onChangeUsdt: handleEntryUsdt_21, ratioValue: entry_ratio_21, onChangeRatio: handleEntryRatio_21 },
      { label: "22th set", usdtValue: entry_usdt_22, onChangeUsdt: handleEntryUsdt_22, ratioValue: entry_ratio_22, onChangeRatio: handleEntryRatio_22 },
      { label: "23th set", usdtValue: entry_usdt_23, onChangeUsdt: handleEntryUsdt_23, ratioValue: entry_ratio_23, onChangeRatio: handleEntryRatio_23 },
      { label: "24th set", usdtValue: entry_usdt_24, onChangeUsdt: handleEntryUsdt_24, ratioValue: entry_ratio_24, onChangeRatio: handleEntryRatio_24 },
    ];

    const circleEntryRowsWithIndex = circleEntryRows.map((row, idx) => ({
      ...row,
      originalIdx: idx,
    }));
    const circleColumnSize = Math.ceil(circleEntryRowsWithIndex.length / 2);
    const circleEntryColumns = [
      circleEntryRowsWithIndex.slice(0, circleColumnSize),
      circleEntryRowsWithIndex.slice(circleColumnSize),
    ];
      
        
    const handleAddSymbol = (symbol) => {
      const confirmed = window.confirm(`${symbol}을 리스트에 추가할까요?`);
      if (confirmed) {
      fetch(`/add_user_symbols_circlepro_front?symbol=${symbol}`)
      .then(res => res.json())
      .then(data => {
        if (data.msg) {
            alert(data.msg); // msg가 존재할 때만 출력
            return;
          }
        setSymbolList(data.signal)
      })
      .catch(err => console.error(err));
      }
    };

    const deleteSymbol = (symbol) => {
      if (symbol === "default") {
        alert('코인을 먼저 선택하세요!');
        return; // Stop execution if the condition is met
      }
      const confirmed = window.confirm(`${symbol}이 포지션이 걸려 있다면 리스트에서 삭제 후 수동으로 포지션 정리해주세요! ${symbol}을 삭제하시겠습니까?`);
      if (confirmed) {
      fetch(`/delete_user_symbols_circlepro?symbol=${symbol}`)
      .then(res => res.json())
      .then(data => setSymbolList(data.signal))
      .catch(err => console.error(err));
      removeListItem(symbol, setE_Items);
      removeListItem(symbol, setW_Items);
      setSymbol('default')
      }
    };

    const handleClosePopup = () => {
      setIsPopupOpen(false);
      setPopupAnchor(null);
    };        

    /////////////////////////////////////////////////////////////////
    
    // const handleCoverratio = (e) => {
    //   const newCoverRatio = e.target.value.trim();
    //   setCoverRatio(newCoverRatio);
    //     const regex = 
    //     /^\d*\.?\d*$/;
    //     if (regex.test(newCoverRatio)) {
    //       setCoverratioValid(true);
    //       } 
    //     else {
    //       setCoverratioValid(false);
    //       }
    //     };

    const addListItem = (newItem, listSetter) => {
      listSetter(prevList => [...prevList, newItem]);
    };

    const removeListItem = (itemName, listSetter) => {
      listSetter(prevList => prevList.filter(item => item !== itemName));
    };
    
    const onClickCoin = (coin) => {
      setSymbol(coin);
      onClickSearch(coin);
    }

    useEffect(() => {

      if(symbolCountValid && fPercentLowValid && fPercentHighValid && fluctuationValid && leverageValid && sensitivityValid && atrperiodValid && sensitivityStandardValid && atrperiodStandardValid && sensitivityCloseValid && atrperiodCloseValid && sensitivityHedgeValid && atrperiodHedgeValid &&
        benefit_ratioratioValid && trigger_ratioValid && entryusdtValid_1 && entryusdtValid_2 && entryusdtValid_3 && entryusdtValid_4 && emaStandardValid &&
        entryusdtValid_5 && entryusdtValid_6 && entryusdtValid_7 && entryusdtValid_8 && entryusdtValid_9 && entryusdtValid_10 && entryusdtValid_11 && entryusdtValid_12 && entryusdtValid_13 &&
        entryusdtValid_14 && entryusdtValid_15 && entryusdtValid_16 && entryusdtValid_17 && entryusdtValid_18 && entryusdtValid_19 && entryusdtValid_20 && 
        entryusdtValid_21 && entryusdtValid_22 && entryusdtValid_23 && entryusdtValid_24 && entryratioValid_2 && entryratioValid_3 && entryratioValid_4 &&
        entryratioValid_5 && entryratioValid_6 && entryratioValid_7 && entryratioValid_8 && entryratioValid_9 && entryratioValid_10 && entryratioValid_11 && entryratioValid_12 && entryratioValid_13 &&
        entryratioValid_14 && entryratioValid_15 && entryratioValid_16 && entryratioValid_17 && entryratioValid_18 && entryratioValid_19 && entryratioValid_20 && 
        entryratioValid_21 && entryratioValid_22 && entryratioValid_23 && entryratioValid_24
        ) {
        setNotAllow(false);
        return;
      }
      setNotAllow(true);
    }, [symbolCountValid, fPercentLowValid, fPercentHighValid, fluctuationValid, leverageValid, sensitivityValid, atrperiodValid, sensitivityStandardValid, atrperiodStandardValid, sensitivityCloseValid, atrperiodCloseValid, sensitivityHedgeValid, atrperiodHedgeValid,
      benefit_ratioratioValid, trigger_ratioValid, entryusdtValid_1, entryusdtValid_2, entryusdtValid_3, entryusdtValid_4, emaStandardValid,
      entryusdtValid_5, entryusdtValid_6, entryusdtValid_7, entryusdtValid_8, entryusdtValid_9, entryusdtValid_10, entryusdtValid_11, entryusdtValid_12, entryusdtValid_13,
      entryusdtValid_14, entryusdtValid_15, entryusdtValid_16, entryusdtValid_17, entryusdtValid_18, entryusdtValid_19, entryusdtValid_20, 
      entryusdtValid_21, entryusdtValid_22, entryusdtValid_23, entryusdtValid_24, entryratioValid_2, entryratioValid_3, entryratioValid_4,
      entryratioValid_5, entryratioValid_6, entryratioValid_7, entryratioValid_8, entryratioValid_9, entryratioValid_10, entryratioValid_11, entryratioValid_12, entryratioValid_13,
      entryratioValid_14, entryratioValid_15, entryratioValid_16, entryratioValid_17, entryratioValid_18, entryratioValid_19, entryratioValid_20, 
      entryratioValid_21, entryratioValid_22, entryratioValid_23, entryratioValid_24
      ]);

    useEffect(() => {
        fetch(`/get_user_symbols_circlepro`)
        .then(res => res.json())
        .then(data => setSymbolList(data.signal))
        .catch(err => console.error(err));
    },[symbol,uid]);

    useEffect(() => {
      if (entry_standard !== 'able' && direction === 'auto') {
        setDirection('long');   // 강제 변경
      }
    }, [entry_standard]);

    const onClickSamaria_check = () => {
      window.open('/SamariaSignals', '_blank');
      return;
    }

    const onClickSearch_auto = () => {
      fetch(`/setdata_first_circlepro`)
      .then((response) => {
        if(!response.ok) {
          throw new Error('400 아니면 500 에러남');
        }
        return response.json()
      })
      .then((result) => {
        if (!result || result.length === 0) {
        console.log("데이터가 비어있음, 그냥 넘어감");
        return; // 아무것도 안 함
      }

        const data = result[0]; // 안전하게 변수로 받음
        console.log('db에서 성공적으로 데이터 받아옴');
        setSymbolCount(data.auto_count);
        setTopBottom(data.top_bottom);
        setFluctuation(data.fluctuation)
        setFundingRate(data.funding_rate);
        setFpercentLow(data.f_percent_low);
        setFpercentHigh(data.f_percent_high);

        setEntryMode(data.entry_mode);
        setHedgeMode(data.hedge_mode);
        setLeverage(data.leverage_info);
        setEntryCandle(data.entry_candle);
        setCloseCandle(data.close_candle);

        setEntrySignal(result[0].entry_signal);
        setEmaStandard(result[0].ema_standard);
        setEmaLosscut(result[0].ema_losscut);

        setEntryChecking(data.entry_checking);
        setStandardChecking(data.standard_checking);
        setCloseChecking(data.close_checking);

        setCloseMode(data.close_mode);

        setSensitivity(data.sensitivity);
        setAtrPeriod(data.atr_period);

        setBasicMode(data.basic_mode);
        setEntryContinue(data.entry_continue);
        setEntryStandard(data.entry_standard);
        setCandleStandard(data.candle_standard);
        setCandleTypeStandard(data.candle_type_standard);
        setSensitivityStandard(data.sensitivity_standard);
        setAtrPeriodStandard(data.atr_period_standard);

        setSensitivityHedge(data.sensitivity_hedge);
        setAtrPeriodHedge(data.atr_period_hedge);
        setHedgeCandle(data.hedge_candle);
        setCandleTypeHedge(data.candle_type_hedge);
        setHedgeChecking(data.hedge_checking);
        
        setSensitivityClose(data.sensitivity_close);
        setAtrPeriodClose(data.atr_period_close);
        
        setDirection(data.direction);
        setCircleBenefitMode(data.circle_benefit_mode);

        setBenefitRatio(data.benefit_ratio);

        setTriggerRatio(data.trigger_ratio);

        setEntryUsdt_1(data.entry_usdt_1);
        setEntryUsdt_2(data.entry_usdt_2);
        setEntryUsdt_3(data.entry_usdt_3);
        setEntryUsdt_4(data.entry_usdt_4);
        setEntryUsdt_5(data.entry_usdt_5);
        setEntryUsdt_6(data.entry_usdt_6);
        setEntryUsdt_7(data.entry_usdt_7);
        setEntryUsdt_8(data.entry_usdt_8);
        setEntryUsdt_9(data.entry_usdt_9);
        setEntryUsdt_10(data.entry_usdt_10);
        setEntryUsdt_11(data.entry_usdt_11);
        setEntryUsdt_12(data.entry_usdt_12);
        setEntryUsdt_13(data.entry_usdt_13);
        setEntryUsdt_14(data.entry_usdt_14);
        setEntryUsdt_15(data.entry_usdt_15);
        setEntryUsdt_16(data.entry_usdt_16);
        setEntryUsdt_17(data.entry_usdt_17);
        setEntryUsdt_18(data.entry_usdt_18);
        setEntryUsdt_19(data.entry_usdt_19);
        setEntryUsdt_20(data.entry_usdt_20);
        setEntryUsdt_21(data.entry_usdt_21);
        setEntryUsdt_22(data.entry_usdt_22);
        setEntryUsdt_23(data.entry_usdt_23);
        setEntryUsdt_24(data.entry_usdt_24);

        setEntryRatio_2(data.entry_ratio_2);
        setEntryRatio_3(data.entry_ratio_3);
        setEntryRatio_4(data.entry_ratio_4);
        setEntryRatio_5(data.entry_ratio_5);
        setEntryRatio_6(data.entry_ratio_6);
        setEntryRatio_7(data.entry_ratio_7);
        setEntryRatio_8(data.entry_ratio_8);
        setEntryRatio_9(data.entry_ratio_9);
        setEntryRatio_10(data.entry_ratio_10);
        setEntryRatio_11(data.entry_ratio_11);
        setEntryRatio_12(data.entry_ratio_12);
        setEntryRatio_13(data.entry_ratio_13);
        setEntryRatio_14(data.entry_ratio_14);
        setEntryRatio_15(data.entry_ratio_15);
        setEntryRatio_16(data.entry_ratio_16);
        setEntryRatio_17(data.entry_ratio_17);
        setEntryRatio_18(data.entry_ratio_18);
        setEntryRatio_19(data.entry_ratio_19);
        setEntryRatio_20(data.entry_ratio_20);
        setEntryRatio_21(data.entry_ratio_21);
        setEntryRatio_22(data.entry_ratio_22);
        setEntryRatio_23(data.entry_ratio_23);
        setEntryRatio_24(data.entry_ratio_24);

        setCandleTypeEntry(data.candle_type_entry);
        setCandleTypeClose(data.candle_type_close);
      })
      .catch((error) => {
        console.error('에러남:', error.message || error);
      })
    }

    const onClickSearch = (symbol) => {
      let urlName
      const str = symbol;
      console.log(str);
      if(str === 'default'){
        urlName = 'default'
      }else{
        const index = str.indexOf('/');
        urlName = str.substring(0,index);
      }  
      fetch(`/setdata_circlepro/${urlName}/${setlevel}`)
      .then((response) => {
        if(!response.ok) {
          throw new Error('400 아니면 500 에러남');
        }
        return response.json()
      })
      .then((result) => {
        const data = result[0]; // 안전하게 변수로 받음
        console.log('db에서 성공적으로 데이터 받아옴');
        setEntryMode(data.entry_mode);
        setHedgeMode(data.hedge_mode);
        setLeverage(data.leverage_info);
        setEntryCandle(data.entry_candle);
        setCloseCandle(data.close_candle);

        setEntrySignal(result[0].entry_signal);
        setEmaStandard(result[0].ema_standard);
        setEmaLosscut(result[0].ema_losscut);

        setEntryChecking(data.entry_checking);
        setStandardChecking(data.standard_checking);
        setCloseChecking(data.close_checking);

        setCloseMode(data.close_mode);

        setSensitivity(data.sensitivity);
        setAtrPeriod(data.atr_period);

        setBasicMode(data.basic_mode);
        setEntryContinue(data.entry_continue);
        setEntryStandard(data.entry_standard);
        setCandleStandard(data.candle_standard);
        setCandleTypeStandard(data.candle_type_standard);
        setSensitivityStandard(data.sensitivity_standard);
        setAtrPeriodStandard(data.atr_period_standard);

        setSensitivityHedge(data.sensitivity_hedge);
        setAtrPeriodHedge(data.atr_period_hedge);
        setHedgeCandle(data.hedge_candle);
        setCandleTypeHedge(data.candle_type_hedge);
        setHedgeChecking(data.hedge_checking);
        
        setSensitivityClose(data.sensitivity_close);
        setAtrPeriodClose(data.atr_period_close);
        
        setDirection(data.direction);
        setCircleBenefitMode(data.circle_benefit_mode);

        setBenefitRatio(data.benefit_ratio);

        setTriggerRatio(data.trigger_ratio);

        setLongCount(data.long_count);
        setShortCount(data.short_count);

        setEntryUsdt_1(data.entry_usdt_1);
        setEntryUsdt_2(data.entry_usdt_2);
        setEntryUsdt_3(data.entry_usdt_3);
        setEntryUsdt_4(data.entry_usdt_4);
        setEntryUsdt_5(data.entry_usdt_5);
        setEntryUsdt_6(data.entry_usdt_6);
        setEntryUsdt_7(data.entry_usdt_7);
        setEntryUsdt_8(data.entry_usdt_8);
        setEntryUsdt_9(data.entry_usdt_9);
        setEntryUsdt_10(data.entry_usdt_10);
        setEntryUsdt_11(data.entry_usdt_11);
        setEntryUsdt_12(data.entry_usdt_12);
        setEntryUsdt_13(data.entry_usdt_13);
        setEntryUsdt_14(data.entry_usdt_14);
        setEntryUsdt_15(data.entry_usdt_15);
        setEntryUsdt_16(data.entry_usdt_16);
        setEntryUsdt_17(data.entry_usdt_17);
        setEntryUsdt_18(data.entry_usdt_18);
        setEntryUsdt_19(data.entry_usdt_19);
        setEntryUsdt_20(data.entry_usdt_20);
        setEntryUsdt_21(data.entry_usdt_21);
        setEntryUsdt_22(data.entry_usdt_22);
        setEntryUsdt_23(data.entry_usdt_23);
        setEntryUsdt_24(data.entry_usdt_24);

        setEntryRatio_2(data.entry_ratio_2);
        setEntryRatio_3(data.entry_ratio_3);
        setEntryRatio_4(data.entry_ratio_4);
        setEntryRatio_5(data.entry_ratio_5);
        setEntryRatio_6(data.entry_ratio_6);
        setEntryRatio_7(data.entry_ratio_7);
        setEntryRatio_8(data.entry_ratio_8);
        setEntryRatio_9(data.entry_ratio_9);
        setEntryRatio_10(data.entry_ratio_10);
        setEntryRatio_11(data.entry_ratio_11);
        setEntryRatio_12(data.entry_ratio_12);
        setEntryRatio_13(data.entry_ratio_13);
        setEntryRatio_14(data.entry_ratio_14);
        setEntryRatio_15(data.entry_ratio_15);
        setEntryRatio_16(data.entry_ratio_16);
        setEntryRatio_17(data.entry_ratio_17);
        setEntryRatio_18(data.entry_ratio_18);
        setEntryRatio_19(data.entry_ratio_19);
        setEntryRatio_20(data.entry_ratio_20);
        setEntryRatio_21(data.entry_ratio_21);
        setEntryRatio_22(data.entry_ratio_22);
        setEntryRatio_23(data.entry_ratio_23);
        setEntryRatio_24(data.entry_ratio_24);

        setCandleTypeEntry(data.candle_type_entry);
        setCandleTypeClose(data.candle_type_close);

        setTradeStart(data.trade_start);
        setBenefit_stop(data.current_symbol_stop);
        setCurrentCoin_stop(data.current_coin_clear);
        setAllCoin_stop(data.all_coin_clear);

        const e_checklist = isItemInList(data.symbol, e_items);
        const w_checklist = isItemInList(data.symbol, w_items);
        
        if(str !== 'default'){
          result.forEach(item => {
            // console.log('item',item)
            if ((item.trade_start === 1 || item.trade_start === '1') && e_checklist === false) {
              addListItem(item.symbol, setE_Items);
            }
            if ((item.trade_start === 0 || item.trade_start === '0') && e_checklist === true) {
              removeListItem(item.symbol, setE_Items);
            }
          
            // Waiting coin 리스트 삽입 및 삭제
            if ((item.current_symbol_stop === 1 || item.current_symbol_stop === '1') && w_checklist === false) {
              addListItem(item.symbol, setW_Items);
            }
            if ((item.current_symbol_stop === 0 || item.current_symbol_stop === '0') && w_checklist === true) {
              removeListItem(item.symbol, setW_Items);
            }
          });
        };

        console.log('e_items',e_items);
        console.log('e_checklist',e_checklist);
      })
      .catch((error) => {
        console.log('에러남');
      })
    }

    const onClickAutoStart = () => {

      let confirmed = false

      confirmed = window.confirm('헷지버전 자동매매를 시작하시겠습니까?');

      if (confirmed) {
        console.log('스타트 버튼 클릭')
        fetch('/start_circlepro_auto', {
          method : 'POST',
          headers: {
            'Content-Type' : 'application/json',
          },
          body: JSON.stringify({
            auto_count,
            top_bottom,
            fluctuation,
            funding_rate,
            f_percent_low,
            f_percent_high,
            entry_mode,
            hedge_mode,
            leverage_info,
            sensitivity,
            atr_period,
            basic_mode,
            entry_continue,
            entry_standard,
            entry_signal,
            ema_standard,
            ema_losscut,
            candle_standard,
            candle_type_standard,
            sensitivity_standard,
            atr_period_standard,
            sensitivity_hedge,
            atr_period_hedge,
            hedge_candle,
            candle_type_hedge,
            hedge_checking,
            sensitivity_close,
            atr_period_close,
            entry_candle,
            entry_checking,
            standard_checking,
            close_checking,
            close_mode,
            close_candle,
            direction,
            circle_benefit_mode,
            benefit_ratio,
            trigger_ratio,
            entry_usdt_1,
            entry_usdt_2,
            entry_usdt_3,
            entry_usdt_4,
            entry_usdt_5,
            entry_usdt_6,
            entry_usdt_7,
            entry_usdt_8,
            entry_usdt_9,
            entry_usdt_10,
            entry_usdt_11,
            entry_usdt_12,
            entry_usdt_13,
            entry_usdt_14,
            entry_usdt_15,
            entry_usdt_16,
            entry_usdt_17,
            entry_usdt_18,
            entry_usdt_19,
            entry_usdt_20,
            entry_usdt_21,
            entry_usdt_22,
            entry_usdt_23,
            entry_usdt_24,
            entry_ratio_2,
            entry_ratio_3,
            entry_ratio_4,
            entry_ratio_5,
            entry_ratio_6,
            entry_ratio_7,
            entry_ratio_8,
            entry_ratio_9,
            entry_ratio_10,
            entry_ratio_11,
            entry_ratio_12,
            entry_ratio_13,
            entry_ratio_14,
            entry_ratio_15,
            entry_ratio_16,
            entry_ratio_17,
            entry_ratio_18,
            entry_ratio_19,
            entry_ratio_20,
            entry_ratio_21,
            entry_ratio_22,
            entry_ratio_23,
            entry_ratio_24,
            candle_type_entry,
            candle_type_close
            }),
         })
         .then(response => response.json())  // 응답을 JSON 형식으로 변환
         .then(data => {
          alert(data.message);  // JSON에서 message 값을 가져옴
         })
         .catch(error => console.error('Error:', error));
       };

      onClickSearch_auto();
      // 첫 번째 실행 (1초 후)
      setTimeout(() => {
        window.location.reload();
      }, 10000); // 첫 실행까지 1초 딜레이
    };

    const onClickStart = () => {

      if (symbol === "default") {
        alert('코인을 먼저 선택하세요!');
        return; // Stop execution if the condition is met
      }
      
      let confirmed = false

      if (trade_start === 0 || trade_start === "0"){
        confirmed = window.confirm(symbol + '의 매매를 시작하시겠습니까?');
      }else if (trade_start === 1 || trade_start === "1"){
        confirmed = window.confirm(symbol + '의 셋팅값을 변경하시겠습니까?');
      }

      if (confirmed) {
        console.log('스타트 버튼 클릭')
        fetch('/start_circlepro', {
          method : 'POST',
          headers: {
            'Content-Type' : 'application/json',
          },
          body: JSON.stringify({
            symbol,
            entry_mode,
            hedge_mode,
            leverage_info,
            sensitivity,
            atr_period,
            basic_mode,
            entry_continue,
            entry_standard,
            entry_signal,
            ema_standard,
            ema_losscut,
            candle_standard,
            candle_type_standard,
            sensitivity_standard,
            atr_period_standard,
            sensitivity_hedge,
            atr_period_hedge,
            hedge_candle,
            candle_type_hedge,
            hedge_checking,
            sensitivity_close,
            atr_period_close,
            entry_candle,
            entry_checking,
            standard_checking,
            close_checking,
            close_mode,
            close_candle,
            direction,
            circle_benefit_mode,
            benefit_ratio,
            trigger_ratio,
            entry_usdt_1,
            entry_usdt_2,
            entry_usdt_3,
            entry_usdt_4,
            entry_usdt_5,
            entry_usdt_6,
            entry_usdt_7,
            entry_usdt_8,
            entry_usdt_9,
            entry_usdt_10,
            entry_usdt_11,
            entry_usdt_12,
            entry_usdt_13,
            entry_usdt_14,
            entry_usdt_15,
            entry_usdt_16,
            entry_usdt_17,
            entry_usdt_18,
            entry_usdt_19,
            entry_usdt_20,
            entry_usdt_21,
            entry_usdt_22,
            entry_usdt_23,
            entry_usdt_24,
            entry_ratio_2,
            entry_ratio_3,
            entry_ratio_4,
            entry_ratio_5,
            entry_ratio_6,
            entry_ratio_7,
            entry_ratio_8,
            entry_ratio_9,
            entry_ratio_10,
            entry_ratio_11,
            entry_ratio_12,
            entry_ratio_13,
            entry_ratio_14,
            entry_ratio_15,
            entry_ratio_16,
            entry_ratio_17,
            entry_ratio_18,
            entry_ratio_19,
            entry_ratio_20,
            entry_ratio_21,
            entry_ratio_22,
            entry_ratio_23,
            entry_ratio_24,
            candle_type_entry,
            candle_type_close
            }),
         })
         .then(response => response.json())  // 응답을 JSON 형식으로 변환
         .then(data => {
          onClickSearch(symbol);
          alert(data.message);  // JSON에서 message 값을 가져옴
         })
         .catch(error => console.error('Error:', error));
       };
    };

    const buildCircleproPresetPayload = () => ({
      entry_mode,
      hedge_mode,
      leverage_info,
      sensitivity,
      atr_period,
      basic_mode,
      entry_continue,
      entry_standard,
      entry_signal,
      ema_standard,
      ema_losscut,
      candle_standard,
      candle_type_standard,
      sensitivity_standard,
      atr_period_standard,
      sensitivity_hedge,
      atr_period_hedge,
      hedge_candle,
      candle_type_hedge,
      hedge_checking,
      sensitivity_close,
      atr_period_close,
      entry_candle,
      entry_checking,
      standard_checking,
      close_checking,
      close_mode,
      close_candle,
      direction,
      circle_benefit_mode,
      benefit_ratio,
      trigger_ratio,
      entry_usdt_1,
      entry_usdt_2,
      entry_usdt_3,
      entry_usdt_4,
      entry_usdt_5,
      entry_usdt_6,
      entry_usdt_7,
      entry_usdt_8,
      entry_usdt_9,
      entry_usdt_10,
      entry_usdt_11,
      entry_usdt_12,
      entry_usdt_13,
      entry_usdt_14,
      entry_usdt_15,
      entry_usdt_16,
      entry_usdt_17,
      entry_usdt_18,
      entry_usdt_19,
      entry_usdt_20,
      entry_usdt_21,
      entry_usdt_22,
      entry_usdt_23,
      entry_usdt_24,
      entry_ratio_2,
      entry_ratio_3,
      entry_ratio_4,
      entry_ratio_5,
      entry_ratio_6,
      entry_ratio_7,
      entry_ratio_8,
      entry_ratio_9,
      entry_ratio_10,
      entry_ratio_11,
      entry_ratio_12,
      entry_ratio_13,
      entry_ratio_14,
      entry_ratio_15,
      entry_ratio_16,
      entry_ratio_17,
      entry_ratio_18,
      entry_ratio_19,
      entry_ratio_20,
      entry_ratio_21,
      entry_ratio_22,
      entry_ratio_23,
      entry_ratio_24,
      candle_type_entry,
      candle_type_close,
    });

    const applyCircleproPreset = (payload = {}) => {
      if (payload.entry_mode !== undefined) setEntryMode(payload.entry_mode);
      if (payload.hedge_mode !== undefined) setHedgeMode(payload.hedge_mode);
      if (payload.leverage_info !== undefined) setLeverage(payload.leverage_info);
      if (payload.sensitivity !== undefined) setSensitivity(payload.sensitivity);
      if (payload.atr_period !== undefined) setAtrPeriod(payload.atr_period);
      if (payload.basic_mode !== undefined) setBasicMode(payload.basic_mode);
      if (payload.entry_continue !== undefined) setEntryContinue(payload.entry_continue);
      if (payload.entry_standard !== undefined) setEntryStandard(payload.entry_standard);
      if (payload.entry_signal !== undefined) setEntrySignal(payload.entry_signal);
      if (payload.ema_standard !== undefined) setEmaStandard(payload.ema_standard);
      if (payload.ema_losscut !== undefined) setEmaLosscut(payload.ema_losscut);
      if (payload.candle_standard !== undefined) setCandleStandard(payload.candle_standard);
      if (payload.candle_type_standard !== undefined) setCandleTypeStandard(payload.candle_type_standard);
      if (payload.sensitivity_standard !== undefined) setSensitivityStandard(payload.sensitivity_standard);
      if (payload.atr_period_standard !== undefined) setAtrPeriodStandard(payload.atr_period_standard);
      if (payload.sensitivity_hedge !== undefined) setSensitivityHedge(payload.sensitivity_hedge);
      if (payload.atr_period_hedge !== undefined) setAtrPeriodHedge(payload.atr_period_hedge);
      if (payload.hedge_candle !== undefined) setHedgeCandle(payload.hedge_candle);
      if (payload.candle_type_hedge !== undefined) setCandleTypeHedge(payload.candle_type_hedge);
      if (payload.hedge_checking !== undefined) setHedgeChecking(payload.hedge_checking);
      if (payload.sensitivity_close !== undefined) setSensitivityClose(payload.sensitivity_close);
      if (payload.atr_period_close !== undefined) setAtrPeriodClose(payload.atr_period_close);
      if (payload.entry_candle !== undefined) setEntryCandle(payload.entry_candle);
      if (payload.entry_checking !== undefined) setEntryChecking(payload.entry_checking);
      if (payload.standard_checking !== undefined) setStandardChecking(payload.standard_checking);
      if (payload.close_checking !== undefined) setCloseChecking(payload.close_checking);
      if (payload.close_mode !== undefined) setCloseMode(payload.close_mode);
      if (payload.close_candle !== undefined) setCloseCandle(payload.close_candle);
      if (payload.direction !== undefined) setDirection(payload.direction);
      if (payload.circle_benefit_mode !== undefined) setCircleBenefitMode(payload.circle_benefit_mode);
      if (payload.benefit_ratio !== undefined) setBenefitRatio(payload.benefit_ratio);
      if (payload.trigger_ratio !== undefined) setTriggerRatio(payload.trigger_ratio);
      if (payload.entry_usdt_1 !== undefined) setEntryUsdt_1(payload.entry_usdt_1);
      if (payload.entry_usdt_2 !== undefined) setEntryUsdt_2(payload.entry_usdt_2);
      if (payload.entry_usdt_3 !== undefined) setEntryUsdt_3(payload.entry_usdt_3);
      if (payload.entry_usdt_4 !== undefined) setEntryUsdt_4(payload.entry_usdt_4);
      if (payload.entry_usdt_5 !== undefined) setEntryUsdt_5(payload.entry_usdt_5);
      if (payload.entry_usdt_6 !== undefined) setEntryUsdt_6(payload.entry_usdt_6);
      if (payload.entry_usdt_7 !== undefined) setEntryUsdt_7(payload.entry_usdt_7);
      if (payload.entry_usdt_8 !== undefined) setEntryUsdt_8(payload.entry_usdt_8);
      if (payload.entry_usdt_9 !== undefined) setEntryUsdt_9(payload.entry_usdt_9);
      if (payload.entry_usdt_10 !== undefined) setEntryUsdt_10(payload.entry_usdt_10);
      if (payload.entry_usdt_11 !== undefined) setEntryUsdt_11(payload.entry_usdt_11);
      if (payload.entry_usdt_12 !== undefined) setEntryUsdt_12(payload.entry_usdt_12);
      if (payload.entry_usdt_13 !== undefined) setEntryUsdt_13(payload.entry_usdt_13);
      if (payload.entry_usdt_14 !== undefined) setEntryUsdt_14(payload.entry_usdt_14);
      if (payload.entry_usdt_15 !== undefined) setEntryUsdt_15(payload.entry_usdt_15);
      if (payload.entry_usdt_16 !== undefined) setEntryUsdt_16(payload.entry_usdt_16);
      if (payload.entry_usdt_17 !== undefined) setEntryUsdt_17(payload.entry_usdt_17);
      if (payload.entry_usdt_18 !== undefined) setEntryUsdt_18(payload.entry_usdt_18);
      if (payload.entry_usdt_19 !== undefined) setEntryUsdt_19(payload.entry_usdt_19);
      if (payload.entry_usdt_20 !== undefined) setEntryUsdt_20(payload.entry_usdt_20);
      if (payload.entry_usdt_21 !== undefined) setEntryUsdt_21(payload.entry_usdt_21);
      if (payload.entry_usdt_22 !== undefined) setEntryUsdt_22(payload.entry_usdt_22);
      if (payload.entry_usdt_23 !== undefined) setEntryUsdt_23(payload.entry_usdt_23);
      if (payload.entry_usdt_24 !== undefined) setEntryUsdt_24(payload.entry_usdt_24);
      if (payload.entry_ratio_2 !== undefined) setEntryRatio_2(payload.entry_ratio_2);
      if (payload.entry_ratio_3 !== undefined) setEntryRatio_3(payload.entry_ratio_3);
      if (payload.entry_ratio_4 !== undefined) setEntryRatio_4(payload.entry_ratio_4);
      if (payload.entry_ratio_5 !== undefined) setEntryRatio_5(payload.entry_ratio_5);
      if (payload.entry_ratio_6 !== undefined) setEntryRatio_6(payload.entry_ratio_6);
      if (payload.entry_ratio_7 !== undefined) setEntryRatio_7(payload.entry_ratio_7);
      if (payload.entry_ratio_8 !== undefined) setEntryRatio_8(payload.entry_ratio_8);
      if (payload.entry_ratio_9 !== undefined) setEntryRatio_9(payload.entry_ratio_9);
      if (payload.entry_ratio_10 !== undefined) setEntryRatio_10(payload.entry_ratio_10);
      if (payload.entry_ratio_11 !== undefined) setEntryRatio_11(payload.entry_ratio_11);
      if (payload.entry_ratio_12 !== undefined) setEntryRatio_12(payload.entry_ratio_12);
      if (payload.entry_ratio_13 !== undefined) setEntryRatio_13(payload.entry_ratio_13);
      if (payload.entry_ratio_14 !== undefined) setEntryRatio_14(payload.entry_ratio_14);
      if (payload.entry_ratio_15 !== undefined) setEntryRatio_15(payload.entry_ratio_15);
      if (payload.entry_ratio_16 !== undefined) setEntryRatio_16(payload.entry_ratio_16);
      if (payload.entry_ratio_17 !== undefined) setEntryRatio_17(payload.entry_ratio_17);
      if (payload.entry_ratio_18 !== undefined) setEntryRatio_18(payload.entry_ratio_18);
      if (payload.entry_ratio_19 !== undefined) setEntryRatio_19(payload.entry_ratio_19);
      if (payload.entry_ratio_20 !== undefined) setEntryRatio_20(payload.entry_ratio_20);
      if (payload.entry_ratio_21 !== undefined) setEntryRatio_21(payload.entry_ratio_21);
      if (payload.entry_ratio_22 !== undefined) setEntryRatio_22(payload.entry_ratio_22);
      if (payload.entry_ratio_23 !== undefined) setEntryRatio_23(payload.entry_ratio_23);
      if (payload.entry_ratio_24 !== undefined) setEntryRatio_24(payload.entry_ratio_24);
      if (payload.candle_type_entry !== undefined) setCandleTypeEntry(payload.candle_type_entry);
      if (payload.candle_type_close !== undefined) setCandleTypeClose(payload.candle_type_close);
    };

  
    const onClickStop = () => {

      let confirmed = false

      confirmed = window.confirm('헷지버전 자동매매를 종료하시겠습니까? 종료하시면 프로그램 자체가 종료되므로 수동으로 포지션 정리하셔야 합니다.');

      if (confirmed) {
        console.log('종료 버튼 클릭')
        fetch('/stop_circlepro_auto', {
          method : 'POST',
          headers: {
            'Content-Type' : 'application/json',
          }
         })
         .then(response => response.json())  // 응답을 JSON 형식으로 변환
         .then(data => {
          alert(data.message);  // JSON에서 message 값을 가져옴
         })
         .catch(error => console.error('Error:', error));
       };

      onClickSearch_auto();
      setTimeout(() => {
            window.location.reload();
          }, 10000); // 첫 실행까지 1초 딜레이
    };

    // 익절 후 정리
    const onClickBenefit_stop = (symbol) => {
      const confirmed = window.confirm('Do you want to clean up the selected coin after profiting?');

      if (confirmed) {
        setBenefit_stop(1);
        let urlName
        const str = symbol;
        console.log(str);
        if(str === 'default'){
          urlName = 'default'
        }else{
          const index = str.indexOf('/');
          urlName = str.substring(0,index);
        }
        fetch(`/setdata_benefit_circlepro/${urlName}/${setlevel}`)
        .then((response) => {
          if(!response.ok) {
            throw new Error('400 아니면 500 에러남');
          }
          return response.json()
        })
        .then((result) => {
          console.log('benefit에서 받아온 데이터');
          onClickSearch(symbol);
        })
        .catch((error) => {
          console.log('에러남');
          })
        };
    }

    // 익절 후 정리 취소 
    const onClickBenefit_cancel = (symbol) => {
      const confirmed = window.confirm('Do you want to cancel clearing after profiting the selected coins?');

      if (confirmed) {
        setBenefit_cancel(1);
        let urlName
        const str = symbol;
        console.log(str);
        if(str === 'default'){
          urlName = 'default'
        }else{
          const index = str.indexOf('/');
          urlName = str.substring(0,index);
        }
        fetch(`/setdata_benefit_cancel_circlepro/${urlName}/${setlevel}`)
        .then((response) => {
          if(!response.ok) {
            throw new Error('400 아니면 500 에러남');
          }
          return response.json()
        })
        .then((result) => {
          console.log('benefit_cancel에서 받아온 데이터');
          onClickSearch(symbol);
        })
        .catch((error) => {
          console.log('에러남');
        })
      };
    }

    // 현재 코인 정리 
    const onClickCurrentCoin_stop = (symbol) => {
      const confirmed = window.confirm('현재 선택하신 코인의 포지션을 정리하시길 원하십니까? 포지션 정리시 약 5~10초 정도 지연될 수도 있습니다.');

      if (confirmed) {
        setCurrentCoin_stop(true);
        const str = symbol;
        console.log(str);
        const index = str.indexOf('/');
        const urlName = str.substring(0,index);
        fetch(`/setdata_current_coin_clear_circlepro/${urlName}`)
        .then((response) => {
          if(!response.ok) {
            throw new Error('400 아니면 500 에러남');
          }
          return response.json()
        })
        .then((result) => {
          console.log('current_stop에서 받아온 데이터');
          // 첫 번째 실행 (1초 후)
          setTimeout(() => {
            window.location.reload();
          }, 2000); // 첫 실행까지 1초 딜레이
          })
        .catch((error) => {
          console.log('에러남');
        })
      };
    }


    // 현재 코인 롱 정리 
    const onClickCurrentCoin_long_stop = (symbol) => {
      const confirmed = window.confirm('현재 선택하신 코인의 롱 포지션을 정리하시길 원하십니까? 포지션 정리시 약 5~10초 정도 지연될 수도 있습니다.');

      if (confirmed) {
        setCurrentCoin_stop(true);
        const str = symbol;
        console.log(str);
        const index = str.indexOf('/');
        const urlName = str.substring(0,index);
        fetch(`/setdata_current_coin_long_clear_circlepro/${urlName}`)
        .then((response) => {
          if(!response.ok) {
            throw new Error('400 아니면 500 에러남');
          }
          return response.json()
        })
        .then((result) => {
          console.log('current_stop에서 받아온 데이터');
          onClickSearch(symbol);
        })
        .catch((error) => {
          console.log('에러남');
        })
      };
    }

    // 현재 코인 숏 정리 
    const onClickCurrentCoin_short_stop = (symbol) => {
      const confirmed = window.confirm('현재 선택하신 코인의 숏 포지션을 정리하시길 원하십니까? 포지션 정리시 약 5~10초 정도 지연될 수도 있습니다.');

      if (confirmed) {
        setCurrentCoin_stop(true);
        const str = symbol;
        console.log(str);
        const index = str.indexOf('/');
        const urlName = str.substring(0,index);
        fetch(`/setdata_current_coin_short_clear_circlepro/${urlName}`)
        .then((response) => {
          if(!response.ok) {
            throw new Error('400 아니면 500 에러남');
          }
          return response.json()
        })
        .then((result) => {
          console.log('current_stop에서 받아온 데이터');
          onClickSearch(symbol);
        })
        .catch((error) => {
          console.log('에러남');
        })
      };
    }


    //로그아웃
    const onClickEnd = () => {
        const uuid = cookies.uuid; // cookies에서 uuid를 얻어옴
        if (!uuid) {
          alert('로그인 정보가 존재하지 않습니다.');
          return;
        }
        // 로그아웃 요청을 서버로 보냅니다.
        fetch('/logout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ userId: uuid })
        })
        .then(response => {
          if (!response.ok) {
            throw new Error('로그아웃 요청 실패');
          }
          console.log('로그아웃 성공');
          alert('Logout is successful.');
          window.location.href = '/';
        })
        .catch(error => {
          console.error('로그아웃 요청 중 오류 발생:', error);
        });
      // UUID를 서버로 보내는 예시 코드
    }

    return (
      <main className="kp-console kp-console--circlepro">
        <div className="kp-console__glare" aria-hidden="true" />
        <section className="kp-console__panel">
          <header className="kp-hero">
            <p className="kp-hero__eyebrow">Bitgenstone auto trade system</p>
            <h1 className="kp-hero__title">CirclePro cockpit</h1>
            <p className="kp-hero__subtitle">
              CirclePro 전략의 자동·일반 셋팅과 펀딩 필터를 한 화면에서 제어하세요. UID 상태와 실시간 리스트로 흐름을 바로 확인할 수 있습니다.
            </p>
            <div className="kp-hero__meta">
              <span className="kp-hero__uid">UID {uid || "—"}</span>
              <span>{nickname ? `${nickname}님 환영합니다` : "계정 정보를 불러오는 중"}</span>
            </div>
          </header>

          <div className="kp-console__layout">
            <div className="kp-console__main">
              <article className="kp-card kp-card--controls">
                <div className="kp-symbol-controls">
                  <div className="kp-symbol-dropdown kp-mode-dropdown" ref={modeDropdownRef}>
                    <button
                      type="button"
                      className={`kp-symbol-dropdown__trigger ${isModeMenuOpen ? "is-open" : ""}`}
                      onClick={toggleModeMenu}
                      aria-haspopup="listbox"
                      aria-expanded={isModeMenuOpen}
                    >
                      <span className="kp-symbol-dropdown__current">{currentModeLabel}</span>
                      <span className="kp-symbol-dropdown__chevron" aria-hidden="true" />
                    </button>
                    {isModeMenuOpen && (
                      <div className="kp-symbol-dropdown__menu" role="listbox">
                        {MODE_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            className={`kp-symbol-dropdown__option ${option.value === mode ? "is-active" : ""}`}
                            onClick={() => handleModeSelect(option.value)}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="kp-symbol-dropdown" ref={symbolDropdownRef}>
                    <button
                      type="button"
                      className={`kp-symbol-dropdown__trigger ${isSymbolMenuOpen ? "is-open" : ""}`}
                      onClick={toggleSymbolMenu}
                      aria-haspopup="listbox"
                      aria-expanded={isSymbolMenuOpen}
                    >
                      <span className="kp-symbol-dropdown__current">{currentSymbolLabel}</span>
                      <span className="kp-symbol-dropdown__chevron" aria-hidden="true" />
                    </button>
                    {isSymbolMenuOpen && (
                      <div className="kp-symbol-dropdown__menu" role="listbox">
                        <button
                          type="button"
                          className={`kp-symbol-dropdown__option ${symbol === "default" ? "is-active" : ""}`}
                          onClick={() => handleSymbolSelect("default")}
                        >
                          symbol select
                        </button>
                        {selectableSymbols.length > 0 ? (
                          <>
                            <div className="kp-symbol-dropdown__divider" />
                            {selectableSymbols.map((sym) => (
                              <button
                                key={sym}
                                type="button"
                                className={`kp-symbol-dropdown__option ${sym === symbol ? "is-active" : ""}`}
                                onClick={() => handleSymbolSelect(sym)}
                              >
                                {sym}
                              </button>
                            ))}
                          </>
                        ) : (
                          <div className="kp-symbol-dropdown__empty">등록된 심볼이 없습니다</div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="kp-symbol-controls__buttons">
                    <button onClick={handleOpenPopup} className="button_benefit_c_ut">
                      추 가
                    </button>
                    <button onClick={() => deleteSymbol(symbol)} className="button_benefit_c_ut">
                      삭 제
                    </button>
                  </div>
                </div>

                <SymbolSearchPopup
                  isOpen={isPopupOpen}
                  onAdd={handleAddSymbol}
                  onClose={handleClosePopup}
                  anchorPosition={popupAnchor}
                />
              </article>
              <AdminSharedPresetPanel
                isAdminAccess={isAdminAccess}
                featureKey="circlepro"
                buildPresetPayload={buildCircleproPresetPayload}
                applyPreset={applyCircleproPreset}
                reloadKey={`${uid}`}
                singleRow
              />

              <article className="kp-card kp-card--form">
                <div className="contentWrap_sp">

            <div className="line"></div>

            <div className="center_text_lever"style={{
                    filter: (mode === 'normal') ? 'blur(5px)' : 'none',
                    }}>Auto mode setting</div>
              <div class="leverage_container">
                <div class="leverage_item" disabled={mode === 'normal'}
                  style={{
                    filter: (mode === 'normal') ? 'blur(5px)' : 'none',
                    }}>자동코인 갯수</div>
                  <input 
                  type="number" 
                  class="leverage_input_basic"
                   disabled={mode === 'normal'}
                  style={{
                    filter: (mode === 'normal') ? 'blur(5px)' : 'none',
                    }}
                  value={auto_count}
                  onChange={handleSymbolCount} />
                <div class="leverage_item" disabled={mode === 'normal'}
                  style={{
                    filter: (mode === 'normal') ? 'blur(5px)' : 'none',
                    }}>상하위 선택</div>
                  <select className="select_samaria" value={top_bottom} 
                   disabled={mode === 'normal'}
                  style={{
                    filter: (mode === 'normal') ? 'blur(5px)' : 'none',
                    }}onChange={handleTopBotton}>
                  <option value="top">상위</option>
                  <option value="bottom">하위</option>
                  </select>
                <div class="leverage_item" disabled={mode === 'normal'}
                  style={{
                    filter: (mode === 'normal') ? 'blur(5px)' : 'none',
                    }}>기준등락률(%)</div>
                  <input 
                  type="number"
                  class="leverage_input_basic"
                   disabled={mode === 'normal'}
                  style={{
                    filter: (mode === 'normal') ? 'blur(5px)' : 'none',
                    }}
                  value={fluctuation}
                  onChange={handleFluctuation} />
                </div>
                <div class="leverage_container leverage_container--funding-range">
                  <div class="leverage_item" disabled={mode === 'normal'}
                  style={{
                    filter: (mode === 'normal') ? 'blur(5px)' : 'none',
                    }}>펀딩비율</div>
                  <select className="select_samaria" value={funding_rate}
                   disabled={mode === 'normal'}
                  style={{
                    filter: (mode === 'normal') ? 'blur(5px)' : 'none',
                    }} onChange={handleFundingRate}>
                  <option value="disable">미적용</option>
                  <option value="able">적용</option>
                  </select>
                  <div class="leverage_item" disabled={mode === 'normal'}
                  style={{
                    filter: (mode === 'normal' || funding_rate === 'disable') ? 'blur(5px)' : 'none',
                    }}>사이값</div>
                  <input 
                  type="number"
                  class="leverage_input_basic"
                  value={f_percent_low}
                  disabled={mode === 'normal' || funding_rate === 'disable'}
                  style={{
                    filter: (mode === 'normal' || funding_rate === 'disable') ? 'blur(5px)' : 'none',
                    }}
                  onChange={handleFpercentLow} />
                  <div class="leverage_item"style={{
                    filter: (mode === 'normal' || funding_rate === 'disable') ? 'blur(5px)' : 'none',
                    }}>부터</div>
                  <input 
                  type="number" 
                  class="leverage_input_basic"
                  value={f_percent_high}
                  disabled={mode === 'normal' || funding_rate === 'disable'}
                  style={{
                    filter: (mode === 'normal' || funding_rate === 'disable') ? 'blur(5px)' : 'none',
                    }}
                  onChange={handleFpercentHigh} />
                  <div class="leverage_item"style={{
                    filter: (mode === 'normal' || funding_rate === 'disable') ? 'blur(5px)' : 'none',
                    }}>까지</div>
                </div>
              
            <div className="line"></div>

            <div className="center_text_lever">Basic setting</div>
              <div class="leverage_container">
                <div class="leverage_item">진입기준</div>
                  <select className="select_samaria" value={entry_mode}
                  onChange={handleEntryMode}>
                  <option value="ut">시그널</option>
                  <option value="kinpoint">킨포인트</option>
                  </select>
                <div class="leverage_item">헷지기준</div>
                  <select className="select_samaria" value={hedge_mode}
                  onChange={handleHedgeMode}>
                  <option value="ut">시그널</option>
                  <option value="kinpoint">킨포인트</option>
                  </select>
                <div class="leverage_item">기준봉</div>
                  <select className="select_samaria" value={entry_candle}
                  onChange={handleEntryCandle}>
                  <option value="5m">5 분봉</option>
                  <option value="15m">15 분봉</option>
                  <option value="30m">30 분봉</option>
                  <option value="1h">1 시간봉</option>
                  <option value="4h">4 시간봉</option>
                  <option value="1d">1 일봉</option>
                  </select>
                </div>
                <div class="leverage_container">
                <div class="leverage_item"
                style={{
                  filter: (entry_mode !== 'ut' && hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}>Sensitivity</div>
                <input 
                type="number"
                class="leverage_input_basic"
                 disabled={entry_mode !== 'ut' && hedge_mode !== 'ut'}
                style={{
                  filter: (entry_mode !== 'ut' && hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}
                value={sensitivity}
                onChange={handleSensitivity} />
                <div class="leverage_item" style={{
                    filter: (entry_mode !== 'ut' && hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                  }}>ATR Period</div>
                <input 
                type="number" 
                class="leverage_input_basic"
                 disabled={entry_mode !== 'ut' && hedge_mode !== 'ut'}
                style={{
                  filter: (entry_mode !== 'ut' && hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}
                value={atr_period}
                onChange={handleAtrperiod} />
              </div>
              <div className="leverage_container">
                <div class="leverage_item"style={{
                    filter: (entry_mode !== 'ut' && hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                  }} >진입/헷지 기준 캔들</div>
                <select className="select_samaria" 
                  value={candle_type_entry}
                   disabled={entry_mode !== 'ut' && hedge_mode !== 'ut'}
                    style={{
                      filter: (entry_mode !== 'ut' && hedge_mode !== 'ut') ? 'blur(5px)' : 'none',
                    }}
                  onChange={handleCandleTypeEntry}>
                  <option value="haikin">하이킨아시</option>
                  <option value="normal">일반봉</option>
                </select>
                <div class="leverage_item">진입 기준 신호 </div>
                <select className="select_samaria" 
                  value={entry_checking}
                  onChange={handleEntryChecking}>
                  <option value="-1">발생</option>
                  <option value="-2">확정</option>
                </select>
                <div class="leverage_item">헷지 기준 신호 </div>
                <select className="select_samaria" 
                  value={hedge_checking}
                  onChange={handleHedgeChecking}>
                  <option value="-1">발생</option>
                  <option value="-2">확정</option>
                </select>
              </div>
              <div className="leverage_container">
                <div class="leverage_item">모드 선택</div>
                <select className="select_samaria" 
                  value={basic_mode} 
                  onChange={handleBasicMode}>
                  <option value="normal">일반모드</option>
                  <option value="single">단방모드</option>
                </select>
                <div class="leverage_item">연속 진입</div>
                <select className="select_samaria" 
                  value={entry_continue} 
                  onChange={handleEntryContinue}>
                  <option value="disable">미적용</option>
                  <option value="able">적용</option>
                </select>
              </div>

              <div className="line"></div>

              <div className="leverage_container">
                <div class="leverage_item">보조 적용</div>
                <select className="select_samaria" value={entry_standard} onChange={handleEntryStandard}>
                <option value="able">적용</option>
                <option value="disable">미적용</option>
                </select>
                <div class="leverage_item" style={{
                  filter: (entry_standard !== 'able') ? 'blur(5px)' : 'none',
                }}>EMA 기준봉갯수</div>
                <input 
                type="number"
                class="leverage_input_basic"
                value={ema_standard}
                onChange={handleEmaStandard}
                disabled={entry_standard !== 'able'}
                style={{
                  filter: (entry_standard !== 'able') ? 'blur(5px)' : 'none',
                }}/>
                <div class="leverage_item" style={{
                  filter: (entry_standard !== 'able') ? 'blur(5px)' : 'none',
                }} >EMA 손절적용</div>
                <select className="select_samaria" value={ema_losscut}
                disabled={entry_standard !== 'able'}
                style={{
                  filter: (entry_standard !== 'able') ? 'blur(5px)' : 'none',
                }}
                onChange={handleEmaLosscut}>
                  <option value="no">미적용</option>
                  <option value="apply">적용</option>
                </select>
              </div>

              <div className="leverage_container">
                <div class="leverage_item" style={{
                  filter: (entry_standard !== 'able') ? 'blur(5px)' : 'none',
                }}>보조 시간봉</div>
                <select className="select_samaria" value={candle_standard}
                disabled={entry_standard !== 'able'}
                style={{
                  filter: (entry_standard !== 'able') ? 'blur(5px)' : 'none',
                }}
                onChange={handleCandleStandard}>
                  <option value="5m">5 분봉</option>
                  <option value="15m">15 분봉</option>
                  <option value="30m">30 분봉</option>
                  <option value="1h">1 시간봉</option>
                  <option value="4h">4 시간봉</option>
                  <option value="1d">1 일봉</option>
                </select>
                <div class="leverage_item" style={{
                  filter: (entry_standard !== 'able') ? 'blur(5px)' : 'none',
                }} >신호 기준</div>
                <select className="select_samaria" value={candle_type_standard}
                disabled={entry_standard !== 'able'}
                style={{
                  filter: (entry_standard !== 'able') ? 'blur(5px)' : 'none',
                }}
                onChange={handleCandleTypeStandard}>
                  <option value="haikin">하이킨아시</option>
                  <option value="normal">일반봉</option>
                </select>
              </div>
              
              <div className="line"></div>
              
              <div class="leverage_container">
                <div class="leverage_item">익절 기준 타입</div>
                <select className="select_samaria" 
                  value={close_mode}
                  onChange={handleCloseMode}>
                  <option value="ut">시그널</option>
                  <option value="kinpoint">킨포인트</option>
                </select>
                <div class="leverage_item">기준봉</div>
                  <select className="select_samaria" value={close_candle}
                  onChange={handleCloseCandle}>
                  <option value="5m">5 분봉</option>
                  <option value="15m">15 분봉</option>
                  <option value="30m">30 분봉</option>
                  <option value="1h">1 시간봉</option>
                  <option value="4h">4 시간봉</option>
                  <option value="1d">1 일봉</option>
                  </select>
                </div>
                <div class="leverage_container">
                <div class="leverage_item"style={{
                  filter: (close_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}>Sensitivity</div>
                <input 
                type="number"
                class="leverage_input_basic"
                value={sensitivity_close}
                disabled={close_mode !== 'ut'}
                style={{
                  filter: (close_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}
                onChange={handleSensitivityClose} />
                <div class="leverage_item"style={{
                  filter: (close_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}>ATR Period</div>
                <input 
                type="number" 
                class="leverage_input_basic"
                value={atr_period_close}
                disabled={close_mode !== 'ut'}
                style={{
                  filter: (close_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}
                onChange={handleAtrperiodClose} />
              </div>
              <div class="leverage_container">
                <div class="leverage_item"style={{
                  filter: (close_mode !== 'ut') ? 'blur(5px)' : 'none',
                }}>익절 기준 캔들</div>
                  <select className="select_samaria" 
                    value={candle_type_close}
                    disabled={close_mode !== 'ut'}
                    style={{
                      filter: (close_mode !== 'ut') ? 'blur(5px)' : 'none',
                    }}
                    onChange={handleCandleTypeClose}>
                    <option value="haikin">하이킨아시</option>
                    <option value="normal">일반봉</option>
                  </select>
                <div class="leverage_item">익절 기준 신호</div>
                  <select className="select_samaria" 
                    value={close_checking}
                    onChange={handleCloseChecking}>
                    <option value="-1">발생</option>
                    <option value="-2">확정</option>
                  </select>
              </div>

              <div className="line"></div>

              <div className="leverage_container">
                <div class="leverage_item">Set Leverage</div>
                  <input 
                  type="number" 
                  class="leverage_input_basic"
                  value={leverage_info}
                  onChange={handleLeverage} />
                <div class="leverage_item">최소 익절률(%)</div>
                  <input 
                  type="number" 
                  class="leverage_input_basic"
                  value={benefit_ratio}
                  onChange={handleBenefitratio} />
              </div>

              <div className="leverage_container">
                <div class="leverage_item">헷지트리거(%)</div>
                  <input 
                  type="number" 
                  class="leverage_input_basic"
                  value={trigger_ratio}
                  onChange={handleTriggerratio} />
                <div class="leverage_item">진입 방향 선택</div>
                  <select className="select_samaria" value={direction} onChange={handleDirection}>
                    <option value='long'>롱</option>
                    <option value='short'>숏</option>
                    <option value='auto'disabled={entry_standard !== 'able'}
                    className={entry_standard !== 'able' ? "blurred-option" : ""}>자동전환</option>
                  </select>
                <div class="leverage_item">서클수익모드</div>
                  <select className="select_samaria" value={circle_benefit_mode} onChange={handleCircleBenefitMode}>
                    <option value='normal'>일반</option>
                    <option value='benefit'>수익</option>
                  </select>
              </div>

            <div className="line"></div>

            <div className="center_text_lever">현재 진입 횟수</div>
              <div class="leverage_container">
                <div class="leverage_item" style={{ color: '#f8fafc' }}>롱진입 횟수</div>
                <input 
                type="number"
                class="leverage_input_basic"
                value={long_count}
                disabled={true} />
                <div class="leverage_item" style={{ color: '#f8fafc' }}>숏진입 횟수</div>
                <input 
                type="number" 
                className="leverage_input_basic"
                value={short_count}
                disabled={true}/>
              </div>
            {/* 레버리지 셋팅  레버리지 셋팅 레버리지 셋팅 레버리지 셋팅 레버리지 셋팅*/}
            <div className="line"></div>
              <div className="center_text_lever">진입 USDT & 기준간격</div>
              <div className="wz-entry-table">
                {circleEntryColumns.map((columnRows, columnIdx) => (
                  <div className="wz-entry-table__column" key={`circle-column-${columnIdx}`}>
                    <div className="leverage_container_center_kp wz-entry-table__header">
                      <div className="leverage_item_kp">순 서</div>
                      <div className="leverage_item_kp">USDT</div>
                      <div className="leverage_item_kp">간격(%)</div>
                    </div>
                    {columnRows.map((row) => {
                      const isDisabled = basic_mode === 'single' && row.originalIdx > 0;
                      const disabledStyle = isDisabled ? { filter: 'blur(5px)' } : undefined;

                      return (
                        <div className="leverage_container_center_kp" key={`circle-entry-${row.label}`}>
                          <div className="leverage_item_kp">{row.label}</div>
                          <input
                            type="number"
                            step="1"
                            className="leverage_input_kp"
                            value={row.usdtValue}
                            onChange={row.onChangeUsdt}
                            disabled={isDisabled}
                            style={disabledStyle}
                          />
                          {row.hideRatio ? (
                            <input
                              type="number"
                              className="leverage_input_kp"
                              style={{ visibility: "hidden" }}
                              disabled
                            />
                          ) : (
                            <input
                              type="number"
                              step="1"
                              className="leverage_input_kp"
                              value={row.ratioValue}
                              onChange={row.onChangeRatio}
                              disabled={isDisabled}
                              style={disabledStyle}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>

            <div className="line"></div>
            
            </div>

            <OpenPositionsSnapshotSection
              endpoint="/circlepro_open_positions_snapshot"
              enabled={Boolean(uid)}
              reloadKey={`${uid}-${isAdminAccess ? "1" : "0"}`}
              title="메인/보조 오픈 포지션"
              updatedAtPrefix="마지막 갱신:"
              loadingRefreshLabel="조회중..."
              onSelectSymbol={onClickCoin}
              selectedSymbol={symbol}
            />
            <div className="line"></div>

            <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                <button onClick={() => onClickAutoStart()} disabled={mode === 'normal'}
                style={{
                    filter: (mode === 'normal') ? 'blur(5px)' : 'none',
                  }} className="button_benefit_c_lever">
                자동 매매 시작
                </button>
                <button onClick={() => onClickStop()} className="button_benefit_c_lever">
                자동 매매 종료
                </button>
            </div>  
            <div className="button_benefit_leverage button_benefit_leverage--circlepro">    
                <button onClick={() => onClickStart()} disabled={mode === 'auto'}
                style={{
                    filter: (mode === 'auto') ? 'blur(5px)' : 'none',
                  }} className="bottomButton_sp_lever">
                일반매매 시작
                </button>
            </div>

            <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                <button onClick={() => onClickBenefit_stop(symbol)} disabled={benefit_stop === 1 || trade_start === 0 || benefit_stop === '1' || trade_start === '0'} className="button_benefit_c_lever">
                익절 후 정리
                </button>
                <button onClick={() => onClickBenefit_cancel(symbol)} disabled={benefit_stop === '0' || benefit_stop === 0} className="button_benefit_c_lever">
                취 소
                </button>
            </div>

            <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                <button onClick={() => onClickCurrentCoin_stop(symbol)} disabled={(symbol === 'default' && trade_start === 0 &&  benefit_stop === 0) || (symbol === 'default' && trade_start === '0' &&  benefit_stop === '0') || setlevel <= 7} className="bottomButton_sp_lever">
                현재 코인 정리
                </button>
                {/* <button onClick={() => onClickAllCoin_stop()} disabled={symbol === 'default' && setlevel <= 7} className="button_benefit_c_lever">
                All symbol close
                </button> */}
            </div>
            <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                <button onClick={() => onClickCurrentCoin_long_stop(symbol)} disabled={symbol === 'default'} className="button_benefit_c_lever">
                LONG강제정리
                </button>
                <button onClick={() => onClickCurrentCoin_short_stop(symbol)} disabled={symbol === 'default'} className="button_benefit_c_lever">
                SHORT강제정리
                </button>
            </div>

            <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                <button onClick={() => onClickEnd()} disabled={notAllow} className="bottomButton_sp_lever">
                로그아웃
                </button>
             </div>
              </article>
            </div>

            <aside className="kp-console__side">
              <article className="kp-card kp-card--list">
                <div className="list_box_title_lever">진입중인 코인 리스트</div>
                <ul className="list_box_lever">
                  {e_items.map((item, index) => (
                    <li key={index} className="list_item" onClick={() => onClickCoin(item)}>
                      {item}
                    </li>
                  ))}
                </ul>
              </article>

              <article className="kp-card kp-card--list">
                <div className="list_box_title_lever">익절 후 정리 리스트</div>
                <ul className="list_box_lever">
                  {w_items.map((item, index) => (
                    <li key={index} className="list_item" onClick={() => onClickCoin(item)}>
                      {item}
                    </li>
                  ))}
                </ul>
              </article>
            </aside>
          </div>
        </section>
      </main>
    );
}